import { useState } from "react";
import {
  Button,
  ErrorMessage,
  Flex,
  InfoBox,
  Label,
  LoadingIndicator,
  ModalV2,
  Select,
  Textarea,
} from "@jsq/ds/core";
import { KnowledgeSourcePreference } from "~/graphql/supergraphEnums";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";
import type { GenerationDefaults, RunScope } from "./useGenerateDraftRun";

const PROMPT_FIELD_ID = "ddq-generate-draft-prompt";

const KNOWLEDGE_SOURCE_LABELS: Record<KnowledgeSourcePreference, string> = {
  [KnowledgeSourcePreference.AnswerBankOnly]: "Answer bank only",
  [KnowledgeSourcePreference.AnswerBankThenDocuments]: "Answer bank, then documents",
  [KnowledgeSourcePreference.FullKnowledgeBase]: "Full knowledge base",
};

export type GenerateFirstDraftModalProps = {
  scope: RunScope;
  defaults: GenerationDefaults | undefined;
  isLoadingDefaults: boolean;
  errorMessage: string | null;
  isSubmitting: boolean;
  onHide: () => void;
  onConfirm: (prompt: string) => void;
};

const warningFor = (scope: RunScope): string =>
  scope.kind === "section"
    ? `Re-running "${scope.sectionTitle}" will overwrite its existing generated answers.`
    : "Re-running this job will overwrite every existing generated answer.";

// Mounted only while open, so the prompt and its lock reset between runs.
export const GenerateFirstDraftModal = ({
  scope,
  defaults,
  isLoadingDefaults,
  errorMessage,
  isSubmitting,
  onHide,
  onConfirm,
}: GenerateFirstDraftModalProps) => {
  const [editedPrompt, setEditedPrompt] = useState<string | null>(null);
  const [isPromptEditable, setIsPromptEditable] = useState(false);

  const serverPrompt = defaults?.prompt ?? "";
  const prompt = editedPrompt ?? serverPrompt;
  const selectedModel =
    defaults?.models.find((model) => model.isDefault) ?? defaults?.models[0];
  // The server's default prompt is "" by design — locking an empty field would be a dead end, so
  // it starts editable whenever there's nothing worth guarding against an accidental edit.
  const isFieldEditable = isPromptEditable || serverPrompt === "";
  const title =
    scope.kind === "section" ? `Re-run "${scope.sectionTitle}"` : "Generate first draft";

  return (
    <ModalV2 show onHide={onHide} title={title} data-testid="ddq-generate-draft-modal">
      <ModalV2.Body>
        {isLoadingDefaults ? (
          <LoadingIndicator data-testid="ddq-generate-draft-defaults-loading" />
        ) : (
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <InfoBox variant="info" title={warningFor(scope)} />
            <InfoBox
              variant="info"
              title="Once you generate the first draft, you won't be able to re-order sections or questions. You can still edit existing questions or add new questions."
            />
            <Select
              full
              inModal
              hideScrollBar
              hasLeftPadding={false}
              disabled
              label="Select model"
              value={selectedModel?.id ?? ""}
              helpText="Model selection arrives with DDQ-227."
              data-testid="ddq-generate-draft-model-select"
            >
              <Select.Section>
                {(defaults?.models ?? []).map((model) => (
                  <Select.Option key={model.label} value={model.id ?? ""}>
                    {model.label}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
            <Select
              full
              inModal
              hideScrollBar
              hasLeftPadding={false}
              disabled
              label="Knowledge source"
              value={defaults?.knowledgeSource ?? ""}
              helpText="Document retrieval arrives with DDQ-138. Generation only draws from the answer bank today."
              data-testid="ddq-generate-draft-knowledge-source-select"
            >
              <Select.Section>
                {Object.values(KnowledgeSourcePreference).map((source) => (
                  <Select.Option key={source} value={source}>
                    {KNOWLEDGE_SOURCE_LABELS[source]}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
            <div>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                className={styles.promptLabelRow}
              >
                <Label htmlFor={PROMPT_FIELD_ID}>Prompt</Label>
                <Button
                  variant="link"
                  displayAsLink
                  isInline
                  childAsIcon={{ type: "pen-to-square" }}
                  className={styles.editPromptButton}
                  aria-label="Edit prompt"
                  disabled={isFieldEditable}
                  onClick={() => setIsPromptEditable(true)}
                  data-testid="ddq-generate-draft-edit-prompt"
                />
              </Flex>
              <Textarea
                isFullWidth
                size="large"
                id={PROMPT_FIELD_ID}
                value={prompt}
                readOnly={!isFieldEditable}
                onSetValue={setEditedPrompt}
                data-testid="ddq-generate-draft-prompt"
              />
            </div>
            {errorMessage && (
              <ErrorMessage data-testid="ddq-generate-draft-error">{errorMessage}</ErrorMessage>
            )}
          </Flex>
        )}
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" gap={SPACING.SMALL} justifyContent="flex-end">
          <Button variant="neutral" onClick={onHide}>
            Cancel
          </Button>
          <Button
            isLoading={isSubmitting}
            disabled={isLoadingDefaults || !prompt}
            onClick={() => onConfirm(prompt)}
            data-testid="ddq-generate-draft-confirm"
          >
            {scope.kind === "section" ? "Re-run section" : "Start generation"}
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
